import React, { useState, useEffect } from 'react'
import { 
  AlertTriangle, 
  CreditCard, 
  Gift, 
  Loader, 
  RefreshCw,
  CheckCircle
} from 'lucide-react'
import { apiClient } from '@/lib/api'

interface CreditWarningProps {
  onCreditChange?: (credits: number) => void
  className?: string
}

export default function CreditWarning({ onCreditChange, className = '' }: CreditWarningProps) {
  const [credits, setCredits] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadCredits = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.request('/users/credits', { method: 'GET' })
      
      if (response.success && response.data) {
        const creditAmount = response.data.total || 0
        setCredits(creditAmount)
        if (onCreditChange) {
          onCreditChange(creditAmount)
        }
      } else {
        throw new Error('Failed to load credits')
      }
    } catch (err) {
      setError('Failed to load credits')
      setCredits(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCredits()
  }, [])

  if (loading) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-2">
          <Loader className="w-4 h-4 animate-spin text-blue-600" />
          <span className="text-sm text-blue-800">Checking credit balance...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
          <button
            onClick={loadCredits}
            className="text-red-600 hover:text-red-700"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  if (credits === null) return null

  if (credits === 0) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-red-900">Insufficient Credits</div>
            <div className="text-sm text-red-800 mt-1">
              You need at least 1 credit to create an interview. 
              <a href="/profile" className="underline hover:text-red-900 ml-1">
                Redeem a coupon on your profile page
              </a>
              {' '}to get credits.
            </div>
            <div className="mt-2 text-xs text-red-700">
              Available test coupons: WELCOME100, TESTDRIVE50, HOLIDAY2024
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (credits <= 5) {
    return (
      <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-yellow-900">Low Credit Balance</div>
            <div className="text-sm text-yellow-800 mt-1">
              You have {credits} credit{credits === 1 ? '' : 's'} remaining. Each interview creation uses 1 credit.
              Consider redeeming a coupon to add more credits.
            </div>
          </div>
          <div className="flex items-center space-x-1 text-yellow-900">
            <CreditCard className="w-4 h-4" />
            <span className="font-medium">{credits}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-800">
            Credit balance: {credits} credits available
          </span>
        </div>
        <div className="flex items-center space-x-1 text-green-900">
          <CreditCard className="w-4 h-4" />
          <span className="font-medium">{credits}</span>
        </div>
      </div>
    </div>
  )
}